import { useCallback, useEffect, useState } from "react";
import { usePagination } from "@/hooks/usePagination";
import { api, formatApiError, formatIDR } from "@/lib/api";
import { toast } from "sonner";
import { MonthNav } from "./MonthNav";
import { CheckCircle2, Pencil, RotateCcw, Search, Trash2, Users, X, Plus } from "lucide-react";
import PaginationControls from "../PaginationControls";

/* ================================================================
   ================== TAB: KASBON SEMENTARA =======================
   Kasbon staff/karyawan pending — bisa dilunaskan atau dihapus.
   ================================================================ */
export function KasbonTab({ month, setMonth, onCashChanged, prevMonthLabel, shiftMonth, inputCls, todayISO }) {
  const [data, setData] = useState({ items: [], total_open: 0, total_settled: 0, total_all: 0, count: 0 });
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState(""); // "" | "open" | "settled"
  const [search, setSearch] = useState("");
  const [openForm, setOpenForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { month };
      if (filterStatus) params.status = filterStatus;
      const res = await api.get("/cashbook/kasbon", { params });
      setData(res.data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Gagal memuat kasbon");
    } finally { setLoading(false); }
  }, [filterStatus, month]);

  useEffect(() => { load(); }, [load]);

  const filtered = data.items.filter((k) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (k.name || "").toLowerCase().includes(q) || (k.description || "").toLowerCase().includes(q);
  });
  const {
    page,
    pageSize,
    totalItems,
    totalPages,
    paginatedItems,
    goToPage,
  } = usePagination(filtered, {
    resetKey: `${month}:${filterStatus}:${search}`,
  });

  const settle = async (k) => {
    if (!window.confirm(`Tandai kasbon ${k.name} (${formatIDR(k.amount)}) sebagai LUNAS?`)) return;
    try {
      await api.put(`/cashbook/kasbon/${k.id}/settle`);
      toast.success("Kasbon dilunaskan · Kas otomatis terpotong");
      await load();
      if (onCashChanged) await onCashChanged();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Gagal"); }
  };

  // May not be used later (settled kasbon pinginnya tidak ditampilkan di tab Kasbon Sementara, 
  // hanya direkap di Jurnal Akuntansi)
  const reopen = async (k) => {
    try {
      await api.put(`/cashbook/kasbon/${k.id}/reopen`);
      toast.success("Kasbon dibuka kembali · Transaksi kas dibatalkan");
      await load();
      if (onCashChanged) await onCashChanged();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Gagal"); }
  };
  const remove = async (k) => {
    if (!window.confirm(`Hapus kasbon ${k.name}?`)) return;
    try {
      await api.delete(`/cashbook/kasbon/${k.id}`);
      toast.success("Kasbon dihapus");
      await load();
      if (onCashChanged) await onCashChanged();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Gagal"); }
  };

  const bulkSettleAll = async () => {
    const totalOpen = Number(data.total_open || 0);
    if (totalOpen <= 0) {
      toast.info("Tidak ada kasbon PENDING untuk dilunaskan.");
      return;
    }
    const msg1 = `Anda akan menandai SEMUA kasbon PENDING sebagai LUNAS sekaligus.\n\nTotal: ${formatIDR(totalOpen)}\n\nAksi ini TIDAK memotong kas otomatis (untuk data lama). Lanjutkan?`;
    if (!window.confirm(msg1)) return;
    const confirm2 = window.prompt('Ketik "LUNAS SEMUA" (huruf besar) untuk konfirmasi:');
    if (confirm2 !== "LUNAS SEMUA") {
      toast.error("Dibatalkan — konfirmasi tidak cocok.");
      return;
    }
    try {
      const res = await api.post("/cashbook/kasbon/settle-all-pending");
      toast.success(`${res.data.settled_count} kasbon ditandai LUNAS`);
      await load();
      if (onCashChanged) await onCashChanged();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Gagal");
    }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-2 md:gap-3 mb-4">
        <div className="flex flex-wrap items-center gap-2 w-full md:w-auto">
          <MonthNav value={month} onChange={setMonth} testIdPrefix="kasbon-month" prevMonthLabel={prevMonthLabel} shiftMonth={shiftMonth} />
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} data-testid="kasbon-status-filter"
            className="rounded-none border border-zinc-300 bg-white px-3 py-2 text-sm focus:border-[#002FA7] focus:outline-none">
            <option value="">Semua Status</option>
            <option value="open">Belum Lunas</option>
            <option value="settled">Sudah Lunas</option>
          </select>
          <div className="relative flex-1 md:flex-none w-full md:w-auto">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cari nama / keterangan…" data-testid="kasbon-search"
              className="rounded-none border border-zinc-300 bg-white pl-10 pr-3 py-2 text-sm w-full md:w-72 focus:border-[#002FA7] focus:outline-none" />
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 w-full md:w-auto">
          {Number(data.total_open || 0) > 0 && (
            <button
              data-testid="kasbon-bulk-settle-all"
              onClick={bulkSettleAll}
              className="rounded-none bg-white text-[#008A00] border border-[#008A00] px-3 md:px-4 py-2 md:py-2.5 text-[11px] md:text-sm font-bold uppercase tracking-wider hover:bg-[#008A00]/5 inline-flex items-center gap-2 flex-1 md:flex-none justify-center"
              title="Tandai SEMUA kasbon PENDING → LUNAS (tanpa memotong kas). Untuk membersihkan data lama."
            >
              <CheckCircle2 className="w-4 h-4" /> <span className="hidden sm:inline">Tandai Semua Lunas</span><span className="sm:hidden">Lunas Semua</span>
            </button>
          )}
          <button data-testid="kasbon-add-button" onClick={() => { setEditing(null); setOpenForm(true); }}
            className="rounded-none bg-[#002FA7] text-white px-3 md:px-5 py-2 md:py-2.5 text-[11px] md:text-sm font-bold uppercase tracking-wider hover:bg-[#001E7A] inline-flex items-center gap-2 flex-1 md:flex-none justify-center">
            <Plus className="w-4 h-4" /> Tambah Kasbon
          </button>
        </div>
      </div>


      {/* Jika semua kasbon sudah lunas, jangan tampilkan kasbon sama sekali */}
      {data.total_open === 0 ? (
        <div className="text-center text-zinc-400 text-xs font-mono py-8 border border-dashed border-zinc-200">Tidak ada kasbon sisa bulan ini</div>
      ) : data.total_open > 0 && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-zinc-200 border border-zinc-200 mb-5">
            <div className="bg-white p-4">
              <div className="text-[10px] uppercase tracking-widest font-bold text-zinc-500">Kasbon Belum Lunas</div>
              <div data-testid="kasbon-total-open" className="font-mono text-2xl font-bold mt-1 text-[#E81123]">{formatIDR(data.total_open)}</div>
            </div>
            <div className="bg-white p-4">
              <div className="text-[10px] uppercase tracking-widest font-bold text-zinc-500">Sudah Dilunaskan</div>
              <div data-testid="kasbon-total-settled" className="font-mono text-2xl font-bold mt-1 text-[#008A00]">{formatIDR(data.total_settled)}</div>
            </div>
            <div className="bg-white p-4">
              <div className="text-[10px] uppercase tracking-widest font-bold text-zinc-500">Total Semua Kasbon Bulan Ini</div>
              <div className="font-mono text-2xl font-bold mt-1 text-zinc-900">{formatIDR(data.total_all)}</div>
            </div>
          </div>

          {/* Desktop table */}
          <div className="hidden md:block border border-zinc-200 bg-white overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="bg-zinc-50 border-b border-zinc-200 text-[11px] font-bold text-zinc-600 uppercase tracking-widest">
                  <th className="px-3 py-3 whitespace-nowrap">Tanggal</th>
                  <th className="px-3 py-3">Nama</th>
                  <th className="px-3 py-3">Keterangan</th>
                  <th className="px-3 py-3 text-right">Jumlah</th>
                  <th className="px-3 py-3">Status</th>
                  <th className="px-3 py-3 text-right">Aksi</th>
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={6} className="px-4 py-10 text-center text-zinc-400 font-mono text-xs">Memuat…</td></tr>}
                {!loading && filtered.length === 0 && (
                  <tr><td colSpan={6} className="px-4 py-12 text-center text-zinc-400 font-mono text-xs">Belum ada kasbon.</td></tr>
                )}
                {paginatedItems.map((k) => {
                  // Backend selalu normalize status ke "PENDING" (belum lunas) atau "PAID" (lunas).
                  // Fallback: legacy value "open"/"settled" tetap didukung (case-insensitive).
                  const _s = String(k.status || "").trim().toUpperCase();
                  const isPaid = _s === "PAID" || _s === "SETTLED";
                  return (
                  <tr key={k.id} data-testid="kasbon-row" className={`border-b border-zinc-100 hover:bg-zinc-50 ${isPaid ? "opacity-60" : ""}`}>
                    <td className="px-3 py-2.5 font-mono text-xs whitespace-nowrap">{k.date}</td>
                    <td className="px-3 py-2.5 text-sm font-semibold text-zinc-900">{k.name}</td>
                    <td className="px-3 py-2.5 text-xs text-zinc-600">{k.description || "—"}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-sm font-bold text-zinc-900">{formatIDR(k.amount)}</td>
                    <td className="px-3 py-2.5">
                      {isPaid ? (
                        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-[#008A00] bg-[#008A00]/10 px-2 py-1">
                          <CheckCircle2 className="w-3 h-3" /> Lunas
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-[#E81123] bg-[#E81123]/10 px-2 py-1">
                          Belum Lunas
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center justify-end gap-1">
                        {!isPaid ? (
                          <button data-testid="kasbon-settle-btn" onClick={() => settle(k)} className="p-1.5 hover:bg-[#008A00]/10 text-[#008A00] border border-[#008A00]/30 rounded" title="Tandai Lunas — ubah status jadi PAID">
                            <CheckCircle2 className="w-3.5 h-3.5" />
                          </button>
                        ) : (
                          <button data-testid="kasbon-reopen-btn" onClick={() => reopen(k)} className="p-1.5 hover:bg-amber-100 text-amber-700" title="Buka kembali (batalkan pelunasan)">
                            <RotateCcw className="w-3.5 h-3.5" />
                          </button>
                        )}
                        <button data-testid="kasbon-edit-btn" onClick={() => { setEditing(k); setOpenForm(true); }} className="p-1.5 hover:bg-zinc-100 text-zinc-700" title="Edit">
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <button data-testid="kasbon-del-btn" onClick={() => remove(k)} className="p-1.5 hover:bg-[#E81123]/10 text-[#E81123]" title="Hapus">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                  );
                })}
                {!loading && filtered.length > 0 && (
                  <tr className="border-t-2 border-zinc-900 bg-zinc-50">
                    <td colSpan={3} className="px-3 py-3">
                      <span className="text-xs font-bold uppercase tracking-widest text-zinc-900">TOTAL {filterStatus === "settled" ? "LUNAS" : filterStatus === "open" ? "BELUM LUNAS" : "SEMUA KASBON"}</span>
                    </td>
                    <td className="px-3 py-3 text-right font-mono font-bold text-lg text-zinc-900">
                      {formatIDR(
                        filtered.reduce((s, k) => s + Number(k.amount || 0), 0)
                      )}
                    </td>
                    <td colSpan={2}></td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile card list */}
          <div className="md:hidden space-y-2">
            {loading && <div className="text-center text-zinc-400 text-xs font-mono py-8">Memuat…</div>}
            {!loading && filtered.length === 0 && (
              <div className="text-center text-zinc-400 text-xs font-mono py-8 border border-dashed border-zinc-200">Belum ada kasbon.</div>
            )}
            {!loading && paginatedItems.map((k) => {
              const _s = String(k.status || "").trim().toUpperCase();
              const isPaid = _s === "PAID" || _s === "SETTLED";
              return (
                <div key={k.id} data-testid="mobile-kasbon-item" className={`border bg-white p-3 ${isPaid ? "border-zinc-200 opacity-70" : "border-l-4 border-l-[#F97316] border-zinc-200"}`}>
                  <div className="flex items-center justify-between gap-2 mb-1.5">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="font-mono text-[10px] text-zinc-500 whitespace-nowrap">{k.date}</span>
                      {isPaid ? (
                        <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-widest font-bold text-[#008A00] bg-[#008A00]/10 px-1.5 py-0.5">
                          <CheckCircle2 className="w-2.5 h-2.5" /> Lunas
                        </span>
                      ) : (
                        <span className="text-[9px] uppercase tracking-widest font-bold text-[#E81123] bg-[#E81123]/10 px-1.5 py-0.5">Belum Lunas</span>
                      )}
                    </div>
                    <div className="flex items-center gap-0.5">
                      {!isPaid ? (
                        <button onClick={() => settle(k)} className="p-1 hover:bg-[#008A00]/10 text-[#008A00]" data-testid="kasbon-settle-mobile" title="Tandai Lunas"><CheckCircle2 className="w-3.5 h-3.5" /></button>
                      ) : (
                        <button onClick={() => reopen(k)} className="p-1 hover:bg-amber-100 text-amber-700" data-testid="kasbon-reopen-mobile" title="Buka kembali"><RotateCcw className="w-3.5 h-3.5" /></button>
                      )}
                      <button onClick={() => { setEditing(k); setOpenForm(true); }} className="p-1 hover:bg-zinc-100 text-zinc-700" data-testid="kasbon-edit-mobile"><Pencil className="w-3.5 h-3.5" /></button>
                      <button onClick={() => remove(k)} className="p-1 hover:bg-[#E81123]/10 text-[#E81123]" data-testid="kasbon-del-mobile"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </div>
                  <div className="text-sm font-semibold text-zinc-900">{k.name}</div>
                  <div className="text-xs text-zinc-600 mt-0.5 break-words">{k.description || "—"}</div>
                  <div className="mt-2 pt-2 border-t border-zinc-100 flex items-center justify-between">
                    <div className="text-[9px] uppercase text-zinc-400">Jumlah</div>
                    <div className="font-mono font-bold text-base text-zinc-900">{formatIDR(k.amount)}</div>
                  </div>
                </div>
              );
            })}
            {!loading && filtered.length > 0 && (
              <div className="bg-zinc-900 text-white p-3 mt-2">
                <div className="text-[10px] uppercase tracking-widest text-zinc-300 font-bold">
                  TOTAL {filterStatus === "settled" ? "LUNAS" : filterStatus === "open" ? "BELUM LUNAS" : "SEMUA KASBON"}
                </div>
                <div className="font-mono font-bold text-lg mt-1">
                  {formatIDR(filtered.reduce((s, k) => s + Number(k.amount || 0), 0))}
                </div>
              </div>
            )}
          </div>

          {!loading && (
            <PaginationControls
              page={page}
              pageSize={pageSize}
              totalItems={totalItems}
              totalPages={totalPages}
              onPageChange={goToPage}
              testIdPrefix="cashbook-kasbon-pagination"
            />
          )}
        </>
      )}

      {openForm && (
        <KasbonFormModal
          initial={editing}
          onClose={() => { setOpenForm(false); setEditing(null); }}
          onSaved={async () => { setOpenForm(false); setEditing(null); await load(); }}
          inputCls={inputCls}
          todayISO={todayISO}
        />
      )}
    </div>
  );
}

function KasbonFormModal({ initial, onClose, onSaved, inputCls, todayISO }) {
  const isEdit = initial && initial.id;
  const [form, setForm] = useState({
    date: initial?.date || todayISO(),
    name: initial?.name || "",
    description: initial?.description || "",
    amount: initial?.amount || 0,
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Nama wajib diisi"); return; }
    if (Number(form.amount) <= 0) { toast.error("Jumlah harus > 0"); return; }
    setSaving(true);
    try {
      const payload = {
        date: form.date,
        name: form.name.trim(),
        description: form.description.trim(),
        amount: Number(form.amount),
      };
      if (isEdit) {
        await api.put(`/cashbook/kasbon/${initial.id}`, payload);
        toast.success("Kasbon diperbarui");
      } else {
        await api.post("/cashbook/kasbon", payload);
        toast.success("Kasbon ditambahkan");
      }
      await onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Gagal menyimpan");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-zinc-900/50 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white border border-zinc-300 w-full max-w-lg">
        <div className="flex items-center justify-between p-5 border-b border-zinc-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center bg-[#002FA7]">
              <Users className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest font-bold text-zinc-500">Kasbon Sementara</div>
              <h3 className="font-bold text-zinc-900 text-lg">{isEdit ? "Edit Kasbon" : "Tambah Kasbon"}</h3>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-zinc-100"><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          <Field label="Tanggal">
            <input data-testid="kasbon-form-date" type="date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className={inputCls} />
          </Field>
          <Field label="Nama Penerima Kasbon">
            <input data-testid="kasbon-form-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="cth: Budi Santoso" className={inputCls} />
          </Field>
          <Field label="Keterangan">
            <input data-testid="kasbon-form-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Untuk keperluan..." className={inputCls} />
          </Field>
          <Field label="Jumlah (Rp)">
            <input data-testid="kasbon-form-amount" type="number" required min="0" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className={inputCls} />
          </Field>
          <div className="flex justify-end gap-2 pt-2 border-t border-zinc-200">
            <button type="button" onClick={onClose} className="rounded-none bg-white border border-zinc-300 px-4 py-2 text-sm hover:bg-zinc-50">Batal</button>
            <button data-testid="kasbon-form-save" type="submit" disabled={saving} className="rounded-none bg-[#002FA7] text-white px-6 py-2 text-sm font-bold uppercase tracking-wider disabled:opacity-40">
              {saving ? "…" : "Simpan"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-widest font-bold text-zinc-700 mb-1">{label}</div>
      {children}
      {hint && <div className="text-[10px] text-zinc-500 mt-1">{hint}</div>}
    </label>
  );
}